import { useState } from 'react';
import { Button, Col, Form, Row, Spinner, Stack } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import {
  BsFileEarmarkCode,
  BsFileEarmarkExcel,
  BsFileEarmarkImage,
  BsFileEarmarkRichtext,
  BsFileEarmarkText,
  BsFileEarmarkWord,
  BsFileEarmarkZip,
  BsFiletypePdf,
  BsTrash3,
  BsUpload
} from 'react-icons/bs';
import { useToast } from '../../../../../../context/ToastContext';
import { FormStepHeader } from '../../../../../../components/forms';
import { FormSelect } from '../../../../../../components/tables/filterForms/FormSelect';
import { useGetAllDocumentsQuery } from '../../../../../../services/endpoints/content/document';
import { Document } from '../../../../../../types/content.type';
import { getErrorMessage } from '../../../../../../utils/utils';
import { StepProps } from '../AddEditSession';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB

export const getFileIcon = (file: File): JSX.Element => {
  const mimeType = file.type ?? '';

  if (mimeType === 'application/pdf') {
    return <BsFiletypePdf size={48} color="#5E6469" />;
  } else if (
    mimeType === 'application/msword' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return <BsFileEarmarkWord size={48} color="#5E6469" />;
  } else if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return <BsFileEarmarkExcel size={48} color="#5E6469" />;
  } else if (mimeType.startsWith('image/')) {
    return <BsFileEarmarkImage size={48} color="#5E6469" />;
  } else if (mimeType === 'text/plain') {
    return <BsFileEarmarkText size={48} color="#5E6469" />;
  } else if (mimeType === 'application/zip' || mimeType === 'application/x-rar-compressed') {
    return <BsFileEarmarkZip size={48} color="#5E6469" />;
  } else if (
    mimeType.startsWith('text/') ||
    mimeType.startsWith('application/javascript') ||
    mimeType === 'application/typescript'
  ) {
    return <BsFileEarmarkCode size={48} color="#5E6469" />;
  } else {
    return <BsFileEarmarkRichtext size={48} color="#5E6469" />;
  }
};

export const formatFileSize = (file: File): string => {
  const sizeInBytes = file.size;

  if (sizeInBytes < 1024) {
    return `${sizeInBytes} bytes`;
  } else if (sizeInBytes < 1024 * 1024) {
    const sizeInKb = sizeInBytes / 1024;
    return `${sizeInKb.toFixed(2)} KB`;
  } else {
    const sizeInMb = sizeInBytes / (1024 * 1024);
    return `${sizeInMb.toFixed(2)} MB`;
  }
};

export const DocumentElement = ({
  file,
  name,
  onRemove,
  disabled
}: {
  file: File;
  name: string;
  onRemove: Function;
  disabled: boolean;
}) => (
  <div
    className="d-flex align-items-center justify-content-between px-lg-4 px-2 py-2"
    style={{
      background: '#F2F2F2',
      border: '1px solid rgba(94, 100, 105, .5)',
      borderRadius: 8
    }}
  >
    <div className="d-flex align-items-center" style={{ width: '90%' }}>
      {getFileIcon(file)}
      <Stack className="ms-2" style={{ maxWidth: '80%' }}>
        <span className="d-lg-none text-secondary fw-semibold text-truncate">{name}</span>
        <span
          className="d-none d-lg-block text-secondary fw-semibold text-truncate"
          style={{ fontSize: '1.425rem' }}
        >
          {name}
        </span>
        <span className="text-secondary">{formatFileSize(file)}</span>
      </Stack>
    </div>

    {!disabled && (
      <BsTrash3
        size={24}
        color="#B81F69"
        style={{ cursor: 'pointer' }}
        onClick={() => onRemove()}
      />
    )}
  </div>
);

export const DocumentsAndFilesForm = ({
  values,
  setFieldValue,
  readOnly
}: StepProps) => {
  const { data, error, isLoading } = useGetAllDocumentsQuery({
    data: { entries: 1000, orderBy: { field: 'id', order: 'desc' } }
  });
  const toast = useToast();
  const [selectedFile, setSelectedFile] = useState<File>();
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;

    setFileError('');

    if (file.size > MAX_FILE_BYTES) {
      setSelectedFile(undefined);
      setFileError('File must not exceed 10 MB.');
      return;
    }

    setSelectedFile(file);
  };

  const handleAddDocument = () => {
    if (!selectedFile) return;

    if (selectedFile.size > MAX_FILE_BYTES) {
      setFileError('File must not exceed 10 MB.');
      return;
    }

    setFieldValue!('files', [...values!.files!, selectedFile]);
    setFieldValue!('file_metadata', [
      ...values!.file_metadata!,
      {
        title: fileName?.trim() === '' ? selectedFile.name : fileName,
        destroy: false
      }
    ]);

    setFileName('');
    setSelectedFile(undefined);
    setFileError('');
  };

  const handleClearSelection = () => {
    setFileName('');
    setSelectedFile(undefined);
    setFileError('');
  };

  if (isLoading) {
    return (
      <div className="w-100 vh-100 d-inline-flex">
        <Spinner variant="primary" className="m-auto" />
      </div>
    );
  }

  if (error || !data || !data.results) {
    toast.addToast(getErrorMessage(error) ?? 'Error loading documents', 'error');
  }

  const stagedFiles = values!.files ?? [];

  const documentOptions =
    data?.results
      ?.filter(
        (document: Document) =>
          document.title && (document.file_file_name || document.filename)
      )
      .map((document: Document) => ({ value: document.id, label: document.title })) ?? [];

  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Documents & Files" showRequiredLegend={false} />
      </Col>
      {/* Left column: Link existing documents */}
      <Col xs={12} lg={6}>
        <Stack gap={2}>
          <h6
            className="fw-bold mb-0"
            style={{ fontSize: '1.125rem', color: '#5E6469' }}
          >
            Link Existing Documents
          </h6>
          <FormSelect
            name="document_ids"
            label="Documents"
            options={documentOptions}
            multiple
            disabled={readOnly}
            loadingError={error ? 'Error loading documents' : undefined}
          />
        </Stack>
      </Col>

      {/* Right column: Session documents + Upload — unified container */}
      <Col xs={12} lg={6} className="mt-3 mt-lg-0">
        <div
          style={{
            border: '1px solid rgba(94, 100, 105, 0.25)',
            borderRadius: 8,
            padding: 16
          }}
        >
          {/* Session documents list */}
          <h6
            className="fw-bold mb-2"
            style={{ fontSize: '1.125rem', color: '#5E6469' }}
          >
            Session Documents
            {stagedFiles.length > 0 && (
              <span
                className="ms-2 fw-normal"
                style={{ fontSize: '0.875rem', color: '#8c9196' }}
              >
                ({stagedFiles.length} file{stagedFiles.length !== 1 ? 's' : ''})
              </span>
            )}
          </h6>

          {stagedFiles.length === 0 && (
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
              No documents added yet
            </p>
          )}

          <Stack gap={2}>
            {stagedFiles.map((file, index) => (
              <DocumentElement
                key={index}
                file={file}
                name={values!.file_metadata![index]?.title ?? file.name}
                onRemove={() => {
                  setFieldValue!(
                    'files',
                    values!.files!.filter((_, i) => i !== index)
                  );
                  setFieldValue!(
                    'file_metadata',
                    values!.file_metadata!.filter((_, i) => i !== index)
                  );
                }}
                disabled={readOnly ?? false}
              />
            ))}
          </Stack>

          {/* Upload new document — inside same container */}
          {!readOnly && (
            <div
              className="mt-3 pt-3"
              style={{ borderTop: '1px solid rgba(94, 100, 105, 0.15)' }}
            >
              <h6
                className="fw-semibold mb-2"
                style={{ fontSize: '0.95rem', color: '#5E6469' }}
              >
                Upload New Document
              </h6>

              {!selectedFile ? (
                <>
                  <Dropzone onDrop={onDrop} />
                  {fileError && <div className="text-danger mt-2">{fileError}</div>}
                </>
              ) : (
                <Stack gap={2}>
                  {/* File preview */}
                  <div
                    className="d-flex align-items-center px-3 py-2"
                    style={{
                      background: '#F2F2F2',
                      borderRadius: 8,
                      border: '1px solid rgba(94, 100, 105, .3)'
                    }}
                  >
                    {getFileIcon(selectedFile)}
                    <Stack className="ms-2" style={{ minWidth: 0 }}>
                      <span className="text-secondary fw-semibold text-truncate">
                        {selectedFile.name}
                      </span>
                      <span className="text-secondary" style={{ fontSize: '0.875rem' }}>
                        {formatFileSize(selectedFile)}
                      </span>
                    </Stack>
                  </div>

                  {/* Title input */}
                  <Form.Group>
                    <Form.Label className="mb-1" style={{ fontSize: '0.875rem' }}>
                      Document Title
                    </Form.Label>
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder={selectedFile.name}
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Leave blank to use the file name
                    </Form.Text>
                  </Form.Group>

                  {fileError && <div className="text-danger">{fileError}</div>}

                  {/* Action buttons */}
                  <div className="d-flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="btn-box-shadow border-0 d-flex align-items-center gap-1"
                      onClick={handleAddDocument}
                    >
                      <BsUpload size={14} />
                      Add to Session
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleClearSelection}
                    >
                      Cancel
                    </Button>
                  </div>
                </Stack>
              )}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

interface DropzoneProps {
  onDrop: Function;
}

const Dropzone: React.FC<DropzoneProps> = ({ onDrop }) => {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => onDrop(acceptedFiles),
    multiple: false
  });

  return (
    <div
      {...getRootProps({ className: 'dropzone' })}
      style={dropzoneStyle}
    >
      <input {...getInputProps()} />
      <Stack style={{ color: 'rgba(94, 100, 105, .5)' }}>
        <span>Drag and drop here</span>
        <span>or</span>
        <span className="fw-bold" style={{ color: '#6098B8' }}>
          Browse
        </span>
      </Stack>
    </div>
  );
};

const dropzoneStyle: React.CSSProperties = {
  border: '1px dashed rgba(94, 100, 105, 0.5)',
  borderRadius: 8,
  padding: 16,
  textAlign: 'center',
  cursor: 'pointer'
};
